// Avancement d'une commande: le serveur calcule (`order.progress`, voir
// backend/app/core/order_progress.py) et l'écran lit. Ce fichier ne refait le
// calcul QUE pour une commande qui n'a pas de `progress` (cache hors ligne
// ancien), avec la même règle:
//
//   reste à affecter = max(0, commandé − max(affecté, livré))
//
// Le livré est un sous-ensemble de l'affecté: on ne le soustrait jamais EN PLUS
// de l'affecté (c'était la double soustraction qui faisait tomber le reste à
// zéro trop tôt).

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const CLOSED_STATUSES = ['DELIVERED', 'INVOICED', 'PAID'];
const ALLOCATABLE_STATUSES = [
  'DRAFT',
  'PENDING',
  'CONFIRMED',
  'ALLOCATED',
  'IN_TRANSIT',
  'PARTIALLY_DELIVERED',
];

/** Avancement recalculé localement, même forme que `order.progress`. */
export function legacyProgress(order) {
  const status = String(order?.status || '').toUpperCase();
  const ordered = num(order?.quantity_liters);
  const cancelled = status === 'CANCELLED';
  const closed = Boolean(order?.delivery_closed_at) || CLOSED_STATUSES.includes(status);
  const trucks = (order?.allocated_trips || []).map((item) => ({
    trip_id: item?.trip_id || null,
    plate: null,
    allocated_liters: num(item?.quantity),
    delivered_liters: 0,
    has_left: false,
  }));
  const allocated = trucks.reduce((sum, truck) => sum + truck.allocated_liters, 0);
  const delivered = num(order?.delivered_quantity);
  const committed = Math.max(allocated, delivered);
  const toAllocate = cancelled || closed ? 0 : Math.max(0, ordered - committed);
  const toDeliver = cancelled || closed ? 0 : Math.max(0, ordered - delivered);
  const started = delivered > 0 || ['IN_TRANSIT', 'PARTIALLY_DELIVERED'].includes(status);

  let state;
  if (cancelled) state = 'ANNULEE';
  else if (closed || (ordered > 0 && delivered >= ordered)) state = 'LIVREE';
  else if (committed <= 0) state = status === 'DRAFT' ? 'BROUILLON' : 'A_AFFECTER';
  else if (toAllocate > 0) state = 'PARTIELLE';
  else if (started) state = 'EN_ROUTE';
  else state = 'AFFECTEE';

  return {
    ordered_liters: ordered,
    allocated_liters: allocated,
    loaded_liters: 0,
    loaded_is_measured: false,
    delivered_liters: delivered,
    to_allocate_liters: toAllocate,
    to_deliver_liters: toDeliver,
    state,
    truck_count: trucks.length,
    departed_truck_count: 0,
    trucks,
    can_allocate:
      !cancelled && !closed && (!status || ALLOCATABLE_STATUSES.includes(status)) && toAllocate > 0,
    source: 'LOCAL',
  };
}

/** L'avancement d'une commande: celui du serveur, sinon le repli local. */
export function progressOf(order) {
  return order?.progress || legacyProgress(order);
}

export function remainingToDeliver(order) {
  return progressOf(order).to_deliver_liters;
}

export function remainingToAllocate(order) {
  return progressOf(order).to_allocate_liters;
}

/**
 * Prochaine action d'une commande. `facture` est la facture active de la
 * commande: `undefined` si on ne la connaît pas encore (on se fie alors au
 * statut enregistré), `null` si on sait qu'il n'y en a pas, sinon la facture.
 * Une facture émise l'emporte sur le statut: la commande n'est plus « à
 * facturer ».
 */
export function nextOrderAction(order, facture) {
  if (order.status === 'CANCELLED') return 'Commande annulée';
  if (facture) {
    const statutFacture = String(facture.status || '').toUpperCase();
    if (statutFacture === 'PAID') return 'Payée';
    if (statutFacture === 'PARTIAL' || statutFacture === 'PARTIALLY_PAID') return 'Paiement partiel';
    return 'Déjà facturée';
  }
  if (facture === undefined) {
    if (order.status === 'PAID') return 'Commande réglée';
    if (order.status === 'INVOICED') return 'Suivre le paiement';
  }
  const { state } = progressOf(order);
  switch (state) {
    case 'ANNULEE':
      return 'Commande annulée';
    case 'LIVREE':
      return 'Prête pour facturation';
    case 'BROUILLON':
      return 'Confirmer la commande';
    case 'A_AFFECTER':
      return 'Affecter un camion';
    case 'PARTIELLE':
      return 'Affecter le reste';
    default:
      return 'Enregistrer la livraison';
  }
}

// --- Répartition (écran Répartition) ----------------------------------------

/**
 * Jamais un volume que le serveur refusera: ni plus que ce que le camion peut
 * porter, ni plus que ce qu'il reste à affecter.
 */
export function clampAllocation(value, truckAvailable, remaining) {
  const asked = Number(value);
  if (!Number.isFinite(asked) || asked <= 0) return 0;
  return Math.max(0, Math.min(asked, num(truckAvailable), num(remaining)));
}

/**
 * « Couvre X % du reste »: rend X, ou `null` quand il n'y a rien à dire. Un
 * « 0 % » affiché sur un camion libre n'apprend rien et inquiète: il n'est
 * jamais rendu.
 */
export function coveragePercent(truck, remaining) {
  if (typeof truck?.score === 'number') return truck.score > 0 ? truck.score : null;
  const rest = num(remaining);
  const available = num(truck?.available_quantity);
  if (rest <= 0 || available <= 0) return null;
  const pct = Math.round(Math.min(100, (available / rest) * 100));
  return pct > 0 ? pct : null;
}

/**
 * Répartition automatique: on remplit les camions dans l'ordre reçu (le
 * serveur les trie du plus gros volume au plus petit) jusqu'à épuisement du
 * reste à affecter. `uncovered` = ce qui restera sans camion.
 */
export function suggestSplit(trucks, remaining) {
  let left = num(remaining);
  const rows = [];
  for (const truck of trucks || []) {
    if (left <= 0) break;
    const take = clampAllocation(truck.available_quantity, truck.available_quantity, left);
    if (take <= 0) continue;
    rows.push({ trip_id: truck.trip_id, quantity: take });
    left -= take;
  }
  return { rows, uncovered: Math.max(0, left) };
}

/**
 * Pourquoi on ne peut pas affecter, en français, ou `null` si on le peut.
 * Les vrais blocages sont au nombre de trois: l'état de la commande, plus rien
 * à affecter, aucun camion libre. Une flotte qui ne couvre qu'une partie du
 * reste n'en est PAS un.
 */
export function allocationBlockReason({ toAllocate, truckCount, blockers = [] }) {
  const erreur = blockers.find((item) => item?.severity === 'error' && item?.message);
  if (erreur) return erreur.message;
  if (num(toAllocate) <= 0) return 'Cette commande est déjà entièrement affectée.';
  if (num(truckCount) <= 0) return 'Aucun camion libre pour le moment.';
  return null;
}
